/**
 * calculateTotalAmount
 * --------------------
 * Computes the total amount the patient has to pay (or the max that can be
 * auto-charged) based on network type and the practice's billing settings.
 *
 * Branching logic:
 *  - in-network      → chargeLimitOptions
 *  - out-of-network  → billingMethodOptions (+ patientPortionOptions for eligibility calc)
 *  - self-pay        → chargeLimitSelfPayOptions
 *
 * Input shape:
 *   {
 *     networkType: 'in-network' | 'out-of-network' | 'self-pay',
 *     services: [{ allowedAmount, selfPayAmount, chargeAmount }],
 *     settings: {
 *       // in-network
 *       chargeLimit?:
 *         'combined-allowable-amount' | 'set-higher-allowable-amount' |
 *         'set-dollar-amount' | 'full-cost' |
 *         'guarantee-full-cost',
 *       percentOfAllowed?: number,
 *       dollarLimit?: number,
 *       // out-of-network
 *       billingMethod?: 'eligibility-calculation' | 'down-payment',
 *       patientPortion?: 'self-pay-amount' | 'charge-amount' | 'allowed-amount-ucr',
 *       eligibility?: { deductibleRemaining, coinsurancePercent, copay },
 *       downPaymentAmount?: number
 *     }
 *   }
 */
import { inRn, sumBy } from 'lib/helpers/utility';

const roundCents = (n) => Math.round(n);

export const getPercentOfAllowedCap = (combinedAllowable, percent) =>
  Math.min(combinedAllowable, Math.round((combinedAllowable * percent) / 100));

const pricePerService = (s, portion) => {
  switch (portion) {
    case 'self-pay-amount':
      return inRn(s?.selfPayAmount);
    case 'charge-amount':
      return inRn(s?.chargeAmount);
    case 'allowed-amount-ucr':
      return inRn(s?.allowedAmount);
    default:
      return 0;
  }
};

export const calculateTotalAmount = (input) => {
  const { networkType, services = [], settings = {} } = input ?? {};

  let total = 0;

  switch (networkType) {
    case 'in-network': {
      switch (settings.chargeLimit) {
        case 'combined-allowable-amount':
          total = sumBy(services, 'allowedAmount');
          break;
        case 'set-higher-allowable-amount': {
          const allowable = sumBy(services, 'allowedAmount');
          const percent = Number(settings.percentOfAllowed) || 0;
          total = percent > 0 ? getPercentOfAllowedCap(allowable, percent) : allowable;
          break;
        }
        case 'set-dollar-amount': {
          const allowable = sumBy(services, 'allowedAmount');
          const dollarLimit = inRn(settings.dollarLimit);
          total = allowable > 0 ? Math.min(allowable, dollarLimit) : dollarLimit;
          break;
        }
        case 'full-cost':
          total = sumBy(services, 'selfPayAmount');
          break;
        default:
          total = 0;
      }
      break;
    }

    case 'out-of-network': {
      if (settings.billingMethod === 'down-payment') {
        total = inRn(settings.downPaymentAmount);
        break;
      }

      // eligibility-calculation
      const base = (services ?? []).reduce(
        (acc, s) => acc + pricePerService(s, settings.patientPortion),
        0
      );
      const elig = settings.eligibility ?? {};
      const deductibleRemaining = inRn(elig.deductibleRemaining);
      const coinsurancePercent = inRn(elig.coinsurancePercent);
      const copay = inRn(elig.copay);

      const deductiblePortion = Math.min(base, Math.max(0, deductibleRemaining));
      const coinsuranceAmount =
        (base - deductiblePortion) * (Math.max(0, coinsurancePercent) / 100);

      total = Math.min(copay + deductiblePortion + coinsuranceAmount, base);
      break;
    }

    case 'self-pay': {
      if (settings.chargeLimit === 'guarantee-full-cost') {
        total = sumBy(services, 'selfPayAmount');
      } else {
        const selfPay = sumBy(services, 'selfPayAmount');
        const dollarLimit = inRn(settings.dollarLimit);
        total = selfPay > 0 ? Math.min(selfPay, dollarLimit) : dollarLimit;
      }
      break;
    }

    default:
      total = 0;
  }

  return roundCents(Math.max(0, total));
};

export default calculateTotalAmount;
